import { useMemo } from "react";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v.trim() !== "") return v;
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];

function fmt(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export type TimelineType =
  | "lab_result"
  | "prescription"
  | "visit_summary"
  | "imaging"
  | "immunization"
  | "procedure"
  | "condition";

export interface TimelineItemData {
  id: string;
  monthKey: string; // "2026-03" — for grouping/sorting
  month: string; // "MARCH 2026"
  title: string;
  rawDate: string; // "2026-03-18" ("" when unknown)
  date: string; // "Mar 18, 2026" ("" when unknown)
  createdAt: string; // formatted record.createdAt
  meta: string; // secondary label (lab / facility / "" when unknown)
  type: TimelineType;
  status: string; // raw FHIR status ("" when unknown)
  performerDisplay: string; // "" when unknown
  displayName: string; // "" when unknown
  record: ParsedRecord;
}

function isImagingReport(f: Record<string, unknown>): boolean {
  const category = str(f.category).toLowerCase();
  const contentType = str(f.attachment_content_type).toLowerCase();
  const name = str(f.display_name).toLowerCase();
  return (
    category.includes("imag") ||
    category.includes("radiol") ||
    contentType.includes("image") ||
    /x-?ray|mri|ct scan|ultrasound|sonogram|mammogram|radiograph/.test(name)
  );
}

function adaptTimelineItem(record: ParsedRecord): TimelineItemData | null {
  const f = record.fields;
  let type: TimelineType;
  let rawDate = "";
  let title = "";
  let meta = "";
  let performerDisplay = "";

  switch (record.resourceType) {
    case "Encounter":
      type = "visit_summary";
      rawDate = firstDate(f.start_date, f.end_date);
      title = firstStr(f.encounter_type, f.encounter_class_display, f.reason_display) || "Visit";
      meta = firstStr(f.location_display, f.service_provider);
      performerDisplay = firstStr(f.practitioner_name, f.practitioner_display);
      break;
    case "MedicationRequest":
      type = "prescription";
      rawDate = firstDate(f.authored_on);
      title = firstStr(f.medication_name, f.medication_codeable_text) || "Medication";
      meta = firstStr(f.dosage_text);
      break;
    case "Immunization":
      type = "immunization";
      rawDate = firstDate(f.occurrence_date);
      title = firstStr(f.vaccine_display) || "Immunization";
      meta = firstStr(f.site, f.route);
      break;
    case "Procedure":
      type = "procedure";
      rawDate = firstDate(f.performed_date, f.performed_start, f.performed_end);
      title = firstStr(f.display_name) || "Procedure";
      meta = firstStr(f.reason_display);
      break;
    case "Condition":
      type = "condition";
      rawDate = firstDate(f.onset_date, f.recorded_date, f.asserted_date);
      title = firstStr(f.display_name) || "Condition";
      meta = firstStr(f.category);
      break;
    case "DiagnosticReport":
      type = isImagingReport(f) ? "imaging" : "lab_result";
      rawDate = firstDate(f.issued_date, f.effective_date);
      title = firstStr(f.display_name, f.code) || (type === "imaging" ? "Imaging study" : "Lab result");
      meta = firstStr(f.performer_display);
      performerDisplay = firstStr(f.performer_display, f.interpreter_display);
      break;
    case "Observation":
      type = "lab_result";
      rawDate = firstDate(f.effective_date, f.issued);
      title = firstStr(f.display_name) || "Lab value";
      meta = firstStr(f.category);
      break;
    default:
      return null;
  }

  if (!rawDate) return null;
  const d = new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;

  return {
    id: record.id,
    monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    month: `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`,
    title,
    rawDate,
    date: fmt(rawDate),
    createdAt: fmt(dateOnly(record.createdAt)) || "—",
    meta,
    type,
    status: firstStr(f.status, f.clinical_status),
    performerDisplay,
    displayName: firstStr(f.display_name),
    record,
  };
}

export interface TimelineResult {
  items: TimelineItemData[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTimeline(): TimelineResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const items = useMemo(() => {
    return records
      .map(adaptTimelineItem)
      .filter((i): i is TimelineItemData => i !== null)
      .sort((a, b) => b.rawDate.localeCompare(a.rawDate));
  }, [records]);
  return { items, isLoading, error, refresh };
}

/** History of one observation (matched by display name) across every date it
 *  appears on — powers the trend chart. Returns [] when there are fewer than
 *  two data points so the caller can show an empty-chart state. */
export function useObservationHistoryByName(name: string): { label: string; value: number }[] {
  const { records } = useParsedRecords();
  return useMemo(() => {
    if (!name) return [];
    const points = records
      .filter(
        (r) =>
          r.resourceType === "Observation" &&
          str(r.fields.display_name).toLowerCase() === name.toLowerCase(),
      )
      .map((r) => {
        const d = firstDate(r.fields.effective_date, r.fields.issued);
        const value = parseFloat(str(r.fields.value_quantity));
        return { d, value };
      })
      .filter((p) => p.d && !Number.isNaN(p.value))
      .sort((a, b) => a.d.localeCompare(b.d));
    if (points.length < 2) return [];
    return points.map((p) => {
      const dt = new Date(`${p.d}T00:00:00`);
      return {
        label: `${MONTHS_SHORT[dt.getMonth()]} '${String(dt.getFullYear()).slice(2)}`,
        value: p.value,
      };
    });
  }, [records, name]);
}

/** Same-day observations for a report — the closest thing to a real "panel". */
export function useObservationsByDate(rawDate: string): {
  componentName: string;
  value: string;
  unit: string;
  range: string;
  rangeLow: number | null;
  rangeHigh: number | null;
}[] {
  const { records } = useParsedRecords();
  return useMemo(() => {
    if (!rawDate) return [];
    return records
      .filter(
        (r) =>
          r.resourceType === "Observation" &&
          firstDate(r.fields.effective_date, r.fields.issued) === rawDate,
      )
      .map((r) => {
        const f = r.fields;
        const low = parseFloat(str(f.reference_range_low));
        const high = parseFloat(str(f.reference_range_high));
        return {
          componentName: firstStr(f.display_name) || "Lab value",
          value: firstStr(f.value_quantity, f.value_string, f.value_codeable_text),
          unit: str(f.value_unit),
          range:
            str(f.reference_range_low) || str(f.reference_range_high)
              ? `${str(f.reference_range_low) || "?"}-${str(f.reference_range_high) || "?"}`
              : "",
          rangeLow: Number.isNaN(low) ? null : low,
          rangeHigh: Number.isNaN(high) ? null : high,
        };
      });
  }, [records, rawDate]);
}
